import React, { useEffect, useState } from "react";
import { collection, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import TabNavigation from "./TabNavigation";
import DataTable from "./DataTable";
import EmptyState from "./EmptyState";
import DailyJobs from "./DailyJobs";
import CustomerManagement from "./CustomerManagement";
import CustomerOverview from "./CustomerOverview";
import Reports from "./Reports";
import Expenses from "./Expenses";
import Estimator from "./Estimator";
import EmailList from "./EmailList";

const Dashboard = () => {
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState("dailyJobs");
  const [deleteLoading, setDeleteLoading] = useState(null);

  // MVP: only customers + jobs
  useEffect(() => {
    const unsubscribers = [];
    const listenToCollection = (colName, setState) => {
      const unsub = onSnapshot(
        collection(db, colName),
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setState(data);
        },
        (error) => console.error(`Error listening to ${colName}:`, error)
      );
      unsubscribers.push(unsub);
    };
    listenToCollection("customers", setCustomers);
    listenToCollection("jobs", setJobs);
    return () => unsubscribers.forEach((unsub) => unsub());
  }, []);

  // Delete document
  const handleDelete = async (collectionName, id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    setDeleteLoading(id);
    try {
      await deleteDoc(doc(db, collectionName, id));
      console.log(`✅ Deleted ${id} from ${collectionName}`);
    } catch (error) {
      console.error("❌ Delete error:", error);
      alert("Failed to delete record. Check Firestore permissions.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const getTabData = () => {
    switch (activeTab) {
      case "customers":
        return { data: customers, collection: "customers" };
      case "dailyJobs":
        return { data: jobs, collection: "jobs" };
      case "overview":
        return { data: [], collection: "" };
      default:
        return { data: [], collection: "" };
    }
  };

  const { data: currentData, collection: currentCollection } = getTabData();

  return (
    <div className="min-h-screen bg-secondary-50 pb-safe">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-xl sm:text-3xl font-bold text-secondary-900 mb-1 sm:mb-2">
            Dashboard
          </h1>
          <p className="text-secondary-600 text-sm sm:text-base">
            Jobs for the day
          </p>
        </div>

        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Table Content */}
        {activeTab === "dailyJobs" ? (
          <DailyJobs />
        ) : activeTab === "reports" ? (
          <Reports />
        ) : activeTab === "expenses" ? (
          <Expenses />
        ) : activeTab === "estimator" ? (
          <Estimator />
        ) : activeTab === "customers" ? (
          <>
            <div className="mb-6">
              <CustomerOverview customers={customers} compact />
            </div>
            <CustomerManagement />
          </>
        ) : activeTab === "emailList" ? (
          <EmailList />
        ) : currentData.length === 0 ? (
          <EmptyState activeTab={activeTab} />
        ) : (
          <DataTable
            currentData={currentData}
            currentCollection={currentCollection}
            activeTab={activeTab}
            onDelete={handleDelete}
            deleteLoading={deleteLoading}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
